import React, { ReactElement, useContext, useMemo } from 'react'
import { CMEDetails as CMEDetailsLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { useFormContext, ValidateResult } from '../../../Vendor/react-hook-form/dist/index.ie11'
import FormRow from '../../Common/Components/FormRow'
import { InputHelp } from '../../Common/Components/InputHelp/InputHelp'
import { SelectOptions } from '../../Common/Components/Selects'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { UserInfoContext } from '../../Common/Contexts'
import {
    CMEDetailsNameSpacing,
    DetailsTabFieldNames,
    DISABLE_AKIXI_LOGIN_FIELD_NAME,
    PARENT_CME_FIELD_NAME,
    PROVIDER_IDS_FIELD_NAME
} from '../FieldName'
import { CMEResponse } from '../../../NonReact/Types/Types'
import { getCME } from '../../../NonReact/common/API/EntityAPIClient'
import { displayError } from '../../Common/Utils/Messages'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { OutlinedSelect } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedSelect'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'

type Props = {
    parentCMEData?: SelectOptions<string>
    cmeID?: string
    isViewOnly?: boolean
    setParentCME: (data: Nullable<CMEResponse>) => void
}

/**
 * CME Details content for the CME add/modift modal.
 */
const CMEDetails: React.FC<Props> = ({ parentCMEData, cmeID, isViewOnly, setParentCME }: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle('CMEDetails')

    // Contexts
    const { trigger, watch } = useFormContext()
    const userInfo = useContext(UserInfoContext)

    const providerIDsValue: Nullable<string> = watch(PROVIDER_IDS_FIELD_NAME)

    const isSSOEnabled: boolean = useMemo((): boolean => isStringNotEmpty(providerIDsValue), [providerIDsValue])

    return (
        <div className='cme-details-tab tab-area'>
            <FormRow>
                <ValidationErrors name={CMEDetailsNameSpacing(DetailsTabFieldNames.DESCRIPTION)}>
                    <OutlinedTextInput
                        labelLeft={language.get(CMEDetailsLanguage.CMEDetails_Description)}
                        inputName={CMEDetailsNameSpacing(DetailsTabFieldNames.DESCRIPTION)}
                        rules={{
                            required: {
                                value: true,
                                message: language?.get(CMEDetailsLanguage.DescriptionIsRequired)
                            }
                        }}
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        onChange={(): Promise<boolean> =>
                            trigger(CMEDetailsNameSpacing(DetailsTabFieldNames.DESCRIPTION))
                        }
                        disabled={isViewOnly}
                    />
                </ValidationErrors>
            </FormRow>
            <FormRow>
                <OutlinedTextInput
                    labelLeft={language.get(CMEDetailsLanguage.Id)}
                    inputName={CMEDetailsNameSpacing(DetailsTabFieldNames.ID)}
                    placeholder={language.get(CMEDetailsLanguage.IdPlaceholder)}
                    disabled={isViewOnly || !!cmeID}
                />
            </FormRow>
            {(!cmeID || (!!cmeID && !!parentCMEData)) && (
                // Display billing tag only when the new CME is being created
                // or if the user is updating child CME.
                <FormRow>
                    <OutlinedTextInput
                        labelLeft={language.get(CMEDetailsLanguage.BillingId)}
                        inputName={CMEDetailsNameSpacing(DetailsTabFieldNames.BILLING_ID)}
                        disabled={isViewOnly}
                    />
                </FormRow>
            )}
            <FormRow>
                {parentCMEData && (!cmeID || userInfo?.Scope.CMEnvironmentID !== cmeID) ? (
                    <OutlinedSelect
                        name={PARENT_CME_FIELD_NAME}
                        label={language.get(CMEDetailsLanguage.ParentCme)}
                        options={parentCMEData}
                        disabled={!IsStringEmpty(cmeID) || isViewOnly}
                        iconComponent={!IsStringEmpty(cmeID) ? <></> : undefined}
                        handleChange={(value: string): void => {
                            if (isStringNotEmpty(value)) {
                                getCME(value, 1000).then(setParentCME).catch(displayError)
                            }
                        }}
                    />
                ) : (
                    <></>
                )}
            </FormRow>
            <FormRow>
                <ValidationErrors name={PROVIDER_IDS_FIELD_NAME}>
                    <InputHelp text={language.get(CMEDetailsLanguage.CMEDetails_ProviderIDS_Help)}>
                        <OutlinedTextInput
                            labelLeft={language.get(CMEDetailsLanguage.CMEDetails_ProviderIDS)}
                            inputName={PROVIDER_IDS_FIELD_NAME}
                            disabled={isViewOnly}
                            rules={{
                                validate: (value: string): ValidateResult => {
                                    if (IsStringEmpty(value)) return true
                                    if (value.split(',').length > 5)
                                        return language.get(
                                            CMEDetailsLanguage.CMEDetails_ProviderIDS_Validation_NoMoreThan5
                                        )
                                    if (!/^[a-zA-Z0-9_-]{1,15}((,|,\s)[a-zA-Z0-9_-]{1,15}){0,4}$/.exec(value))
                                        return language.get(
                                            CMEDetailsLanguage.CMEDetails_ProviderIDS_Validation_IDsInvalid
                                        )

                                    return true
                                }
                            }}
                        />
                    </InputHelp>
                </ValidationErrors>
            </FormRow>
            {isSSOEnabled ? (
                <FormRow>
                    <RouterCheckbox
                        name={DISABLE_AKIXI_LOGIN_FIELD_NAME}
                        labelLeft={language.get(CMEDetailsLanguage.CMEDetails_Disable_Akixi_Login)}
                        disabled={isViewOnly}
                    />
                </FormRow>
            ) : (
                <></>
            )}
        </div>
    )
}

export default CMEDetails
